<script>
import { mapActions, mapState, mapGetters } from "vuex";

import NavigationWrapper from "@/components/NavigationWrapper";
import CardTableLinkIndicator from "@/components/CardTableLinkIndicator";

import {
  formatHashrate,
  formatRelativeDate,
  deferSortingToController,
} from "@/utils";

// Number of roles to show on each page
const PAGE_SIZE = 15;

export default {
  // Included components
  components: {
    NavigationWrapper,
    CardTableLinkIndicator,
  },

  data() {
    return {
      // The current page index (1-indexed)
      currentPage: null,

      // The current sort (either "created", "name", or "last_triggered")
      sortBy: "created",

      // Whether to sort descending (true: descending, false: ascending)
      sortDesc: false,
    };
  },

  computed: {
    // The number of pages to display
    pageCount() {
      return Math.ceil(Object.values(this.roles).length / PAGE_SIZE);
    },

    // The current page of roles
    page() {
      // Make a shallow-copy of the cached roles
      let roles = Object.values(this.roles);

      // Sort that copy based on sortBy and sortDesc
      roles.sort((a, b) => {
        if (a[this.sortBy] > b[this.sortBy]) {
          return this.sortDesc ? -1 : 1;
        } else if (a[this.sortBy] === b[this.sortBy]) {
          return 0;
        } else {
          return this.sortDesc ? 1 : -1;
        }
      });

      // Return a slice of the sorted roles, yielding a single page
      return roles.slice(
        (this.currentPage - 1) * PAGE_SIZE,
        this.currentPage * PAGE_SIZE
      );
    },

    // Copies of role.enabled to allow for indirect manipulation
    enabledShadows() {
      let enabledShadows = {};
      Object.values(this.roles).forEach((role) => {
        enabledShadows[role.id] = role.enabled;
      });
      return enabledShadows;
    },

    // Bind the state's roles to this controller
    ...mapState(["roles"]),
    ...mapGetters(["can"]),
  },

  // When the component is loaded, fetch the roles
  async created() {
    // Get the current page number from the URL, or default to 1
    this.currentPage = this.$route.query.page || 1;

    // Get the roles from the API
    await Promise.all([this.getRoles("id,account_count,name,description")]);
  },

  methods: {
    // Go to the details page for the role
    goToRole(role) {
      this.$router.push({ name: "role", params: { roleId: role.id } });
    },

    // Toggle the role (enabled vs. disabled)
    toggleRole(role) {
      if (!this.enabledShadows[role.id]) {
        this.enableRole({ roleId: role.id });
      } else {
        this.disableRole({ roleId: role.id });
      }
    },

    // Other helper functions and actions
    formatRelativeDate,
    formatHashrate,
    deferSortingToController,
    ...mapActions(["getRoles", "enableRole", "disableRole"]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/team">Settings</b-breadcrumb-item>
      <b-breadcrumb-item active>Roles</b-breadcrumb-item>
    </template>
    <b-row class="mb-2">
      <b-col>
        <h1>Roles</h1>
      </b-col>
      <b-col align-self="center">
        <b-button class="float-right" variant="primary" to="/team/roles/create">
          Add Role
        </b-button>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-card class="Card-table Card-table-hoverable mb-3">
          <b-table
            responsive
            small
            :fields="[
              {
                key: 'name',
                label: 'Name',
                sortable: true,
                thStyle: { width: '30%' },
              },
              {
                key: 'description',
                label: 'Description',
                thStyle: { width: '40%' },
              },
              {
                key: 'account_count',
                label: 'Members',
                thStyle: { width: '10%' },
                sortable: true,
              },
            ]"
            :items="page"
            @row-clicked="goToRole"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-compare="deferSortingToController"
          >
            <template #cell(name)="{ item }">
              <b-link :to="`/team/roles/${item.id}`">{{ item.name }}</b-link>
              <CardTableLinkIndicator />
            </template>
          </b-table>
        </b-card>
        <b-pagination-nav
          v-if="pageCount > 1"
          use-router
          base-url="?page="
          v-model="currentPage"
          size="sm"
          :number-of-pages="pageCount"
          :hide-goto-end-buttons="pageCount < 3"
          align="center"
          limit="7"
        ></b-pagination-nav>
      </b-col>
    </b-row>
  </NavigationWrapper>
</template>

<style scoped></style>
